import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssistantAccess } from '@/hooks/useAssistantAccess';
import {
  useAssistantPresentation,
  type AssistantPresentation,
} from '@/hooks/useAssistantPresentation';
import { useAuth } from '@/hooks/useAuth';
import { supportAssistantService } from '@/services/support-assistant.service';
import { isAssistantShortcut } from '@/utils/shortcut';
import {
  consumeGopherDeepLink,
  GOPHER_DEEP_LINK_PARAM,
  hasGopherDeepLink,
  hasGopherLaunched,
  isGopherNewWindow,
} from '@/utils/gopherLaunch';
import type {
  AssistantContextTarget,
  AssistantModuleContext,
  AssistantScope,
} from '@/types/support-assistant';

export type DesktopMode = 'docked' | 'rail' | 'closed';
export type PanelView = 'chat' | 'history';

const MODE_KEY_PREFIX = 'box-os-assistant-panel-mode';
const isDesktopMode = (v: unknown): v is DesktopMode =>
  v === 'docked' || v === 'rail' || v === 'closed';

function readMode(key: string): DesktopMode {
  try {
    const raw = localStorage.getItem(key);
    return isDesktopMode(raw) ? raw : 'closed';
  } catch {
    return 'closed';
  }
}

function writeMode(key: string, mode: DesktopMode) {
  try {
    localStorage.setItem(key, mode);
  } catch {
    // storage unavailable: mode simply isn't persisted
  }
}

export interface AssistantContextValue {
  access: { hasAccess: boolean; enabled: boolean };
  presentation: AssistantPresentation;
  desktopMode: DesktopMode;
  overlayOpen: boolean;
  isOpen: boolean;
  view: PanelView;
  activeSessionId: string | null;
  pageContext: AssistantScope | null;
  moduleContext: AssistantModuleContext | null;
  pinned: AssistantContextTarget | null;
  effectiveScope: AssistantScope | null;
  effectiveContext: AssistantContextTarget | null;
  unread: boolean;
  refsVisible: boolean;
  openPanel: () => void;
  togglePanel: () => void;
  collapse: () => void;
  expandFromRail: () => void;
  closeOverlay: () => void;
  setDesktopMode: (mode: DesktopMode) => void;
  showHistory: () => void;
  showChat: () => void;
  selectSession: (id: string) => void;
  startNewConversation: () => Promise<void>;
  registerContext: (scope: AssistantScope) => void;
  clearContext: (id: string) => void;
  registerModuleContext: (ctx: AssistantModuleContext | null) => void;
  pin: () => void;
  unpin: () => void;
  notifyActivity: () => void;
  setRefsVisible: (v: boolean) => void;
  showNewPill: boolean;
  spotlightVisible: boolean;
  firstRunActive: boolean;
  dismissSpotlight: () => void;
  tryGopherFromSpotlight: () => void;
  completeFirstRun: () => void;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(
  undefined,
);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const access = useAssistantAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const presentation = useAssistantPresentation();
  const storageKey = `${MODE_KEY_PREFIX}:${user?.id ?? 'anon'}`;

  const [desktopMode, setDesktopModeState] = useState<DesktopMode>(() =>
    readMode(storageKey),
  );
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [view, setView] = useState<PanelView>('chat');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [pageContext, setPageContext] = useState<AssistantScope | null>(null);
  const [moduleContext, setModuleContext] =
    useState<AssistantModuleContext | null>(null);
  const [pinned, setPinned] = useState<AssistantContextTarget | null>(null);
  const [unread, setUnread] = useState(false);
  const [refsVisible, setRefsVisible] = useState(false);
  const [spotlightSeenLocal, setSpotlightSeenLocal] = useState(false);
  const [firstRunDoneLocal, setFirstRunDoneLocal] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const storageKeyRef = useRef(storageKey);
  const creatingRef = useRef(false);
  const sessionRef = useRef<string | null>(null);
  sessionRef.current = activeSessionId;
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const pageRef = useRef(pageContext);
  pageRef.current = pageContext;
  const moduleRef = useRef(moduleContext);
  moduleRef.current = moduleContext;

  // Re-read the persisted mode when the signed-in user changes.
  useEffect(() => {
    if (storageKeyRef.current !== storageKey) {
      storageKeyRef.current = storageKey;
      setDesktopModeState(readMode(storageKey));
      // Never carry one user's session, pin or cached threads to the next.
      setActiveSessionId(null);
      setPinned(null);
      setView('chat');
      setOverlayOpen(false);
      setUnread(false);
      setSpotlightSeenLocal(false);
      setFirstRunDoneLocal(false);
      queryClient.removeQueries({ queryKey: ['assistant-session'] });
      queryClient.removeQueries({ queryKey: ['assistant-sessions'] });
      queryClient.removeQueries({ queryKey: ['assistant-empty-state'] });
    }
  }, [storageKey, queryClient]);

  const setDesktopMode = useCallback((mode: DesktopMode) => {
    setDesktopModeState(mode);
    writeMode(storageKeyRef.current, mode);
  }, []);

  // Presentation change: carry "open" across docked <-> overlay/sheet.
  const prevPresentation = useRef(presentation);
  useEffect(() => {
    const prev = prevPresentation.current;
    prevPresentation.current = presentation;
    if (prev === presentation) return;
    if (prev === 'docked' && presentation !== 'docked') {
      setOverlayOpen(desktopMode === 'docked');
    } else if (prev !== 'docked' && presentation === 'docked') {
      // Carry an open overlay/sheet back to the docked panel.
      if (overlayOpen) setDesktopMode('docked');
      setOverlayOpen(false);
    }
  }, [presentation, desktopMode, overlayOpen, setDesktopMode]);

  const isOpen =
    presentation === 'docked' ? desktopMode === 'docked' : overlayOpen;

  useEffect(() => {
    if (isOpen) setUnread(false);
  }, [isOpen]);

  const createSession = useCallback(
    async (scope: AssistantScope | null) => {
      if (creatingRef.current) return;
      creatingRef.current = true;
      try {
        const session = await supportAssistantService.createSession(
          scope ? { scopeType: scope.type, scopeId: scope.id } : undefined,
        );
        setActiveSessionId(session.id);
        setView('chat');
        queryClient.invalidateQueries({ queryKey: ['assistant-sessions'] });
      } finally {
        creatingRef.current = false;
      }
    },
    [queryClient],
  );

  const entityOnly = (t: AssistantContextTarget | null): AssistantScope | null =>
    t && t.type !== 'module' ? t : null;
  const effectiveScope = entityOnly(pinned) ?? pageContext;
  const effectiveContext: AssistantContextTarget | null =
    pinned ?? pageContext ?? moduleContext;

  const openPanel = useCallback(() => {
    if (!access.enabled) return;
    if (presentation === 'docked') setDesktopMode('docked');
    else setOverlayOpen(true);
    if (!sessionRef.current) {
      void createSession(
        entityOnly(pinnedRef.current) ?? pageRef.current,
      ).catch(
        () => undefined,
      );
    }
  }, [access.enabled, presentation, setDesktopMode, createSession]);

  const togglePanel = useCallback(() => {
    if (!access.enabled) return;
    if (presentation === 'docked') {
      if (desktopMode === 'docked') setDesktopMode('closed');
      else openPanel();
    } else if (overlayOpen) {
      setOverlayOpen(false);
    } else {
      openPanel();
    }
  }, [access.enabled, presentation, desktopMode, overlayOpen, setDesktopMode, openPanel]);

  const collapse = useCallback(() => setDesktopMode('rail'), [setDesktopMode]);
  const expandFromRail = useCallback(() => openPanel(), [openPanel]);
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);
  const showHistory = useCallback(() => setView('history'), []);
  const showChat = useCallback(() => setView('chat'), []);
  const selectSession = useCallback((id: string) => {
    setActiveSessionId(id);
    setView('chat');
  }, []);

  const startNewConversation = useCallback(async () => {
    setPinned(null);
    pinnedRef.current = null;
    setActiveSessionId(null);
    sessionRef.current = null;
    setView('chat');
    await createSession(pageRef.current);
  }, [createSession]);

  const registerContext = useCallback((scope: AssistantScope) => {
    setPageContext(scope);
  }, []);
  const clearContext = useCallback((id: string) => {
    setPageContext((prev) => (prev && prev.id === id ? null : prev));
  }, []);
  const registerModuleContext = useCallback(
    (ctx: AssistantModuleContext | null) => setModuleContext(ctx),
    [],
  );
  const pin = useCallback(
    () => setPinned(pageRef.current ?? moduleRef.current),
    [],
  );
  const unpin = useCallback(() => setPinned(null), []);

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const notifyActivity = useCallback(() => {
    if (!isOpenRef.current) setUnread(true);
  }, []);

  const launchDate = access.onboarding.launchDate;

  // Refresh the clock so the NEW pill disappears without a reload.
  useEffect(() => {
    if (!launchDate) return;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [launchDate]);

  const showNewPill = access.enabled && isGopherNewWindow(launchDate, now);
  const spotlightVisible =
    access.enabled &&
    hasGopherLaunched(launchDate, now) &&
    !access.onboarding.spotlightSeen &&
    !spotlightSeenLocal &&
    !isOpen;
  const firstRunActive =
    access.enabled && !access.onboarding.firstRunDone && !firstRunDoneLocal;

  const markSpotlightSeen = useCallback(() => {
    setSpotlightSeenLocal(true);
    // Silent on failure: UI proceeds optimistically, retried on next load.
    void supportAssistantService
      .markSpotlightSeen()
      .catch(() => undefined)
      .then(() =>
        queryClient.invalidateQueries({ queryKey: ['assistant-status'] }),
      );
  }, [queryClient]);

  const dismissSpotlight = markSpotlightSeen;

  const tryGopherFromSpotlight = useCallback(() => {
    markSpotlightSeen();
    openPanel();
  }, [markSpotlightSeen, openPanel]);

  const completeFirstRun = useCallback(() => {
    setFirstRunDoneLocal(true);
    void queryClient.invalidateQueries({ queryKey: ['assistant-status'] });
  }, [queryClient]);

  const spotlightVisibleRef = useRef(spotlightVisible);
  spotlightVisibleRef.current = spotlightVisible;
  const markSeenRef = useRef(markSpotlightSeen);
  markSeenRef.current = markSpotlightSeen;
  const openPanelRef = useRef(openPanel);
  openPanelRef.current = openPanel;

  // ?gopher=open deep link (also restored after a login redirect).
  const handledSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!access.loaded) return;
    const fromUrl = hasGopherDeepLink(location.search);
    if (handledSearchRef.current === location.search && fromUrl) return;
    if (!fromUrl && !consumeGopherDeepLink()) return;
    handledSearchRef.current = location.search;
    if (access.enabled) {
      openPanelRef.current();
      if (!access.onboarding.spotlightSeen) markSeenRef.current();
    }
    if (fromUrl) {
      const params = new URLSearchParams(location.search);
      params.delete(GOPHER_DEEP_LINK_PARAM);
      const remaining = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: remaining ? `?${remaining}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [
    access.loaded,
    access.enabled,
    access.onboarding.spotlightSeen,
    location.search,
    location.pathname,
    location.hash,
    navigate,
  ]);

  // Cmd (Mac) / Ctrl (elsewhere) + J toggles the assistant from anywhere.
  const toggleRef = useRef(togglePanel);
  toggleRef.current = togglePanel;
  useEffect(() => {
    if (!access.enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      if (isAssistantShortcut(e)) {
        e.preventDefault();
        // Pressing the shortcut counts as "Try it" (D-05).
        if (spotlightVisibleRef.current) markSeenRef.current();
        toggleRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [access.enabled]);

  const value = useMemo<AssistantContextValue>(
    () => ({
      access: { hasAccess: access.hasAccess, enabled: access.enabled },
      presentation,
      desktopMode,
      overlayOpen,
      isOpen,
      view,
      activeSessionId,
      pageContext,
      moduleContext,
      pinned,
      effectiveScope,
      effectiveContext,
      unread,
      refsVisible,
      openPanel,
      togglePanel,
      collapse,
      expandFromRail,
      closeOverlay,
      setDesktopMode,
      showHistory,
      showChat,
      selectSession,
      startNewConversation,
      registerContext,
      clearContext,
      registerModuleContext,
      pin,
      unpin,
      notifyActivity,
      setRefsVisible,
      showNewPill,
      spotlightVisible,
      firstRunActive,
      dismissSpotlight,
      tryGopherFromSpotlight,
      completeFirstRun,
    }),
    [
      access.hasAccess,
      access.enabled,
      presentation,
      desktopMode,
      overlayOpen,
      isOpen,
      view,
      activeSessionId,
      pageContext,
      moduleContext,
      pinned,
      effectiveScope,
      effectiveContext,
      unread,
      refsVisible,
      openPanel,
      togglePanel,
      collapse,
      expandFromRail,
      closeOverlay,
      setDesktopMode,
      showHistory,
      showChat,
      selectSession,
      startNewConversation,
      registerContext,
      clearContext,
      registerModuleContext,
      pin,
      unpin,
      notifyActivity,
      showNewPill,
      spotlightVisible,
      firstRunActive,
      dismissSpotlight,
      tryGopherFromSpotlight,
      completeFirstRun,
    ],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return ctx;
}

export function useAssistantOptional(): AssistantContextValue | undefined {
  return useContext(AssistantContext);
}
